import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AttributeRequest, FilterRequest, RetailerAttributeDefinitionResponse, RetailerAttributeDefinitionsService, RetailerAttributeValueDetailsResponse, RetailerAttributeValueResponse } from '@src/app/shared/backend/clients';
import { AttributeFieldValue, DynamicAttributeDefinition, DynamicAttributeOption, DynamicAttributeSelection } from '@src/app/shared/components/attribute-values-form/attribute-values-form.model';
import { AttributeDataType } from '@src/app/shared/enums/AttributeDataType.enum';
import { FilterOperator } from '@src/app/shared/enums/FilterOperator.enum';
import { RetailerAttributeDisplayItem } from './retailer-attribute-display.model';

@Injectable({
  providedIn: 'root'
})
export class RetailerAttributeValuesAdapter {

  constructor(
    private retailerAttributeDefinitionsService:RetailerAttributeDefinitionsService
  ) { }

  async getActiveDefinitions(): Promise<DynamicAttributeDefinition[]> {
    const filter: FilterRequest = {
      pageNumber: 1,
      pageSize: 500,
      filters: [
        {
          propertyName: 'isActive',
          operator: FilterOperator.EQUALS,
          values: ['true']
        }
      ]
    };

    const response = await firstValueFrom(this.retailerAttributeDefinitionsService.apiRetailerAttributeDefinitionsFilterPost(filter));
    const definitions = response.results ?? [];

    return this.mapDefinitions(definitions);
  }

  mapSavedValuesToSelections(savedValues: RetailerAttributeValueResponse[] | null | undefined): DynamicAttributeSelection[] {

    // store definitions values
    const valuesByDefinition = new Map<number, AttributeFieldValue[]>();

    const values = savedValues ?? [];

    // convert backend value to form value
    for (const savedValue of values) {
      const definitionId = savedValue.retailerAttributeDefinition?.id;

      if (definitionId === undefined || definitionId === null) {
        continue;
      }

      const attributeValue = this.mapSavedValue(savedValue);

      const valueIsEmpty = attributeValue === null || attributeValue === undefined || attributeValue === '';

      if(valueIsEmpty) {
        continue;
      }

      const definitionValues = valuesByDefinition.get(definitionId) ?? [];
      definitionValues.push(attributeValue);

      valuesByDefinition.set(
        definitionId,
        definitionValues
      );
    }

    const selections: DynamicAttributeSelection[] = [];

    // convert each definition and values into a form selection (expected by AttributeValuesFormComponent)
    valuesByDefinition.forEach((attributeValues, attributeDefinitionId) => {
      const selection: DynamicAttributeSelection = {
        attributeDefinitionId,
        values: attributeValues
      };

      selections.push(selection);
    });

    return selections;
  }

  mapSelectionsToRequest(selections: DynamicAttributeSelection[]): AttributeRequest[] {
    const requests: AttributeRequest[] = [];

    // convert form selections to backend req
    for (const selection of selections) {
      const request: AttributeRequest = {
        attributeDefinitionId:selection.attributeDefinitionId,
        values:selection.values
      };

      requests.push(request);
    }

    return requests;
  }

  mapDetailsToDisplayItems(definitions: DynamicAttributeDefinition[], savedValues: RetailerAttributeValueDetailsResponse[] | null | undefined, language: string ): RetailerAttributeDisplayItem[] {
    const values = savedValues ?? [];
    const displayItems: RetailerAttributeDisplayItem[] = [];

    for (const definition of definitions) {
      const matchingValues = values.filter(savedValue => {
        return (
          savedValue.retailerAttributeDefinitionId === definition.id
        );
      });

      const translatedValues = matchingValues.map(savedValue => {
        return this.getTranslatedValue(savedValue, language);
      });

      let displayValue = '-';

      if (translatedValues.length > 0) {
        displayValue = translatedValues.join(', ');
      }

      // prepare i18n att definitions
      displayItems.push({
        code: definition.definitionCode,
        name: this.getTranslatedDefinitionName(definition,language),
        value: displayValue,
        changesOverTime: definition.changesOverTime
      });
    }

    return displayItems;
  }

  private mapSavedValue(savedValue: RetailerAttributeValueResponse): AttributeFieldValue | null {
    const definition = savedValue.retailerAttributeDefinition;
    const dataType = definition?.retailerAttributeDataType;

    // fixed values use option identifiers
    if (dataType === AttributeDataType.FIXED_VALUES) {
      return savedValue.id ?? null;
    }

    const valueCode = savedValue.valueCode;

    const isNumeric = dataType === AttributeDataType.INTEGER || dataType === AttributeDataType.DECIMAL;

    if (!isNumeric) {
      return valueCode;
    }

    // convert numeric codes back to numbers - needed by form control type number!
    const normalisedValue = valueCode.replace(',', '.');
    const numericValue = Number(normalisedValue);

    if (Number.isNaN(numericValue)) {
      return valueCode;
    }

    return numericValue;
  }

  private mapDefinitions(definitions: RetailerAttributeDefinitionResponse[]): DynamicAttributeDefinition[] {
    const mappedDefinitions: DynamicAttributeDefinition[] = [];

    for (const definition of definitions) {
      if (definition.id === undefined || definition.id === null) {
        continue;
      }

      const translations =
        definition.retailerAttributeDefinitionTranslations?.map(
          translation => {
            return {
              isoCode: translation.isoCode,
              translatedName: translation.translatedName
            };
          }
        ) ?? [];

      // att definitions converted for the shared dynamic form (AttributeValuesFormComponent)
      mappedDefinitions.push({
        id: definition.id,
        definitionCode: definition.definitionCode,
        dataType: definition.retailerAttributeDataType,
        allowMultipleValues: definition.allowMultipleValues,
        changesOverTime: definition.changesOverTime,
        translations,
        options: this.mapOptions(definition)
      });
    }

    return mappedDefinitions;
  }

  private mapOptions(definition: RetailerAttributeDefinitionResponse): DynamicAttributeOption[] {
    const mappedOptions: DynamicAttributeOption[] = [];
    const values = definition.retailerAttributeValues ?? [];

    for (const value of values) {
      if (value.id === undefined || value.id === null) {
        continue;
      }

      const translations =
        value.retailerAttributeValueTranslations?.map(
          translation => {
            return {
              isoCode: translation.isoCode,
              translatedValue: translation.translatedValue
            };
          }
        ) ?? [];

      // convert FIXED_VALUE options to dropdown options
      mappedOptions.push({
        id: value.id,
        valueCode: value.valueCode,
        isActive: value.isActive,
        translations
      });
    }

    return mappedOptions;
  }

  private getTranslatedDefinitionName(definition: DynamicAttributeDefinition, language: string ): string {
    const currentTranslation = definition.translations.find(
      translation => translation.isoCode === language
    );

    if (currentTranslation?.translatedName) {
      return currentTranslation.translatedName;
    }

    const englishTranslation = definition.translations.find(
      translation => translation.isoCode === 'en'
    );

    if (englishTranslation?.translatedName) {
      return englishTranslation.translatedName;
    }

    return definition.definitionCode;
  }

  private getTranslatedValue(savedValue: RetailerAttributeValueDetailsResponse, language: string): string {
    const translations = savedValue.retailerAttributeValueTranslations ?? [];

    const currentTranslation = translations.find(
      translation => translation.isoCode === language
    );

    if (currentTranslation?.translatedValue) {
      return currentTranslation.translatedValue;
    }

    const englishTranslation = translations.find(
      translation => translation.isoCode === 'en'
    );

    if (englishTranslation?.translatedValue) {
      return englishTranslation.translatedValue;
    }

    return savedValue.valueCode;
  }

}
